import { get, isNil, isNull } from 'lodash';
import { call, put, select, takeLeading } from 'redux-saga/effects';
import { callApi, request } from 'helpers/saga-helpers';
import userToken from 'containers/services/security/access-token-storage';
import {
  setLawyerNotifications,
  setManagerNotifications,
  setListings,
  setIntervenants,
  setLinkToBeneficiaryCodes,
  setExpertNotifications,
  setNotificationOpeningManagerMixedClaim,
  setNotificationsInboxes,
  setCountNotificationsManager,
  requestGetCountNotificationsInboxes,
  setInboxesLawyerNotifications,
  setCountInboxesLawyerNotifications,
} from 'containers/AppContainer/actions';
import {
  REQUEST_GET_LAWYER_NOTIFICATIONS,
  REQUEST_GET_MANAGER_NOTIFICATIONS,
  REQUEST_LINK_BENEFICIARY_CODES,
  REQUEST_LISTINGS,
  REQUEST_LISTINGS_INTERVENANTS,
  REQUEST_READ_LAWYER_NOTIFICATION,
  REQUEST_READ_MANAGER_NOTIFICATION,
  REQUEST_GET_EXPERT_NOTIFICATIONS,
  REQUEST_READ_EXPERT_NOTIFICATION,
  REQUEST_NOTIFICATION_OPENING_MANAGER_MIXED_CLAIM,
  REQUEST_FORCE_UNLOCK_CLAIM,
  REQUEST_READ_CONVERT_CLAIM_NOTIFICATION,
  REQUEST_REFRESH_MANAGER,
  REQUEST_GET_NOTIFICATIONS_INBOXES,
  REQUEST_GET_COUNT_NOTIFICATIONS_MANAGER,
  REQUEST_GET_INBOXES_LAWYER_NOTIFICATIONS,
  REQUEST_GET_COUNT_INBOXES_LAWYER_NOTIFICATIONS,
} from 'containers/AppContainer/constants';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import { LOADERS } from 'containers/AppContainer/Loader/constants';
import currentUserStorage from 'containers/services/security/current-user-storage';

export function* fetchListings(): Generator<unknown> {
  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());
  if (!token) return;
  const path = get(userToken.get(), 'token')
    ? '/corporeal/v2/lawyer-listings'
    : '/corporeal/v2/lawyer-listings?isIntervenant=true';
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(path, {}, options, [], setListings);
}

export function* requestListingsSaga() {
  yield takeLeading(REQUEST_LISTINGS, fetchListings);
}

export function* fetchIntervenants(): Generator<unknown> {
  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());
  if (isNull(token)) return;
  const path = get(userToken.get(), 'token')
    ? '/sagis/intervenants'
    : '/sagis/intervenants?isIntervenant=true';
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  yield callApi(path, {}, options, [], setIntervenants);
}

export function* requestIntervenantsSaga() {
  yield takeLeading(REQUEST_LISTINGS_INTERVENANTS, fetchIntervenants);
}

export function* fetchLinkToBeneficiaryCodes(): Generator<unknown> {
  const token = get(userToken.get(), 'token')
    ? get(userToken.get(), 'token')
    : yield select(makeSelectKeycloakToken());
  if (isNull(token)) return;
  const path = get(userToken.get(), 'token')
    ? '/sagis/linkToBeneficiaryCodes'
    : '/sagis/linkToBeneficiaryCodes?isIntervenant=true';
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  yield callApi(path, {}, options, [], setLinkToBeneficiaryCodes);
}

export function* requestLinkToBeneficiaryCodesSaga() {
  yield takeLeading(
    REQUEST_LINK_BENEFICIARY_CODES,
    fetchLinkToBeneficiaryCodes,
  );
}

export function* getLawyerNotifications(): Generator<unknown> {
  const path = `/corporeal/notification-lawyer/get-lawyer-notifications`;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setLawyerNotifications,
    undefined,
    false,
    LOADERS.NOTIFICATION,
  );
}

export function* getLawyerNotificationsSaga() {
  yield takeLeading(REQUEST_GET_LAWYER_NOTIFICATIONS, getLawyerNotifications);
}

export function* getManagerNotifications(action: any) {
  const { payload } = action;
  const path = `/corporeal/notification-manager/get-manager-notifications?page=${payload.page}&pageSize=40&assignedManagerCode=${payload.assignedManagerCode}&isCourtMonitoringManager=${payload.isMonitoringCourtManager}&isManagerCourtMonitoringManager=${payload.isManagerCourtMonitoringManager}&isManagerOpeningManager=${payload.isManagerOpeningManager}&isManagerPreOpening=${payload.isPreOpeningManager}&isManagerTransaction=${payload.isTransactionManager}&userPrivilegesId=${payload.userPrivilegesId}`;

  const token = get(userToken.get(), 'token');

  if (!token) return;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setManagerNotifications,
    undefined,
    false,
    LOADERS.NOTIFICATION,
  );
}

export function* readLawyerNotification(action: {
  type: string;
  id: number;
}): Generator<unknown> {
  const path = `/corporeal/notification-lawyer/validate-lawyer-notification`;
  const { id } = action;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  };

  yield callApi(path, {}, options);
}

export function* readManagerNotification(action: {
  type: string;
  payload: any;
}): Generator<unknown> {
  const path = `/corporeal/notification-manager/validate-manager-notification`;
  const { payload } = action;

  const token = get(userToken.get(), 'token');

  if (!token) return;

  const options = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...payload }),
  };

  const errorResponse = yield callApi(path, {}, options);
  if (!errorResponse) {
    const inboxes = userPrivileges
      .getUserInboxes()
      .map((el: any) => el.managerInboxesId);
    const transactionAreaInboxes = userPrivileges
      .getUserInboxes()
      .map((el: any) =>
        !isNil(el.transactionInboxeAreaId)
          ? el.transactionInboxeAreaId
          : undefined,
      )
      .filter((element: any) => element);
    if (inboxes) {
      yield put(
        requestGetCountNotificationsInboxes({
          inboxes,
          transactionAreaInboxes,
          isPriorityClaimsManager: userPrivileges.userHasPrivilege(
            PRIVILEGES.ISPRIORITYCLAIMSMANAGER,
          ),
        }),
      );
    }
  }
}

export function* readManagerNotificationSaga() {
  yield takeLeading(REQUEST_READ_MANAGER_NOTIFICATION, readManagerNotification);
}

export function* getManagerNotificationsSaga() {
  yield takeLeading(REQUEST_GET_MANAGER_NOTIFICATIONS, getManagerNotifications);
}

export function* readLawyerNotificationSaga() {
  yield takeLeading(REQUEST_READ_LAWYER_NOTIFICATION, readLawyerNotification);
}

export function* getExpertNotifications(): Generator<unknown> {
  const path = `/corporeal/notification-expert/get-notifications`;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(path, {}, options, [], setExpertNotifications);
}

export function* getExpertNotificationsSaga() {
  yield takeLeading(REQUEST_GET_EXPERT_NOTIFICATIONS, getExpertNotifications);
}
export function* readExpertNotification(action: {
  type: string;
  id: number;
}): Generator<unknown> {
  const path = `/corporeal/notification-expert/validate-notification`;
  const { id } = action;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  };

  yield callApi(path, {}, options);
}
export function* readExpertNotificationSaga() {
  yield takeLeading(REQUEST_READ_EXPERT_NOTIFICATION, readExpertNotification);
}
export function* getNotificationOpeningManagerMixedClaim(): Generator<unknown> {
  const path = `/corporeal/opening-manager-mixed-claim`;

  const token = get(userToken.get(), 'token');

  if (!token) return;
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(path, {}, options, [], setNotificationOpeningManagerMixedClaim);
}

export function* getNotificationOpeningManagerMixedClaimSaga() {
  yield takeLeading(
    REQUEST_NOTIFICATION_OPENING_MANAGER_MIXED_CLAIM,
    getNotificationOpeningManagerMixedClaim,
  );
}
export function* requestForceUnlockClaim(action: {
  type: string;
  payload: any;
  successCallBack: Function;
}): Generator<unknown> {
  const path = `/corporeal/force-unlock-claim`;

  const token = get(userToken.get(), 'token');
  if (!token) return;
  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ claimNumber: action.payload.claimNumber }),
  };

  const errorResponse = yield callApi(path, {}, options, []);
  if (!errorResponse) {
    yield action.successCallBack();
  }
}
export function* requestForceUnlockClaimSaga() {
  yield takeLeading(REQUEST_FORCE_UNLOCK_CLAIM, requestForceUnlockClaim);
}
export function* requestReadConvertClaimNotification(action: {
  type: string;
  payload: any;
}): Generator<unknown> {
  const path = `/corporeal/opening-manager-mixed-claim/read`;

  const token = get(userToken.get(), 'token');
  if (!token) return;
  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...action.payload }),
  };

  const errorResponse = yield callApi(path, {}, options, []);
  if (!errorResponse) {
    const inboxes = userPrivileges
      .getUserInboxes()
      .map((el: any) => el.managerInboxesId);
    const transactionAreaInboxes = userPrivileges
      .getUserInboxes()
      .map((el: any) =>
        !isNil(el.transactionInboxeAreaId)
          ? el.transactionInboxeAreaId
          : undefined,
      )
      .filter((element: any) => element);
    if (inboxes) {
      yield put(
        requestGetCountNotificationsInboxes({
          inboxes,
          transactionAreaInboxes,
          isPriorityClaimsManager: userPrivileges.userHasPrivilege(
            PRIVILEGES.ISPRIORITYCLAIMSMANAGER,
          ),
        }),
      );
    }
  }
}
export function* requestReadConvertClaimNotificationSaga() {
  yield takeLeading(
    REQUEST_READ_CONVERT_CLAIM_NOTIFICATION,
    requestReadConvertClaimNotification,
  );
}

export function* requestRefreshManagerInfo(action: {
  type: string;
  successCallBack: Function;
}): Generator<unknown> {
  const username = currentUserStorage.get().username;
  const path = `/corporeal/user-privileges/corporeal-manager-privileges?name=${username}`;

  const token = get(userToken.get(), 'token');
  if (!token) return;
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  const priveliges = yield call(request as any, path, options);
  currentUserStorage.set({
    username: get(currentUserStorage.get(), 'username', ''),
    privileges: priveliges as any,
    subservices: get(currentUserStorage.get(), 'subservices', ''),
  });
  const inboxes = userPrivileges
    .getUserInboxes()
    .map((el: any) => el.managerInboxesId);
  const transactionAreaInboxes = userPrivileges
    .getUserInboxes()
    .map((el: any) =>
      !isNil(el.transactionInboxeAreaId)
        ? el.transactionInboxeAreaId
        : undefined,
    )
    .filter((element: any) => element);
  if (inboxes) {
    yield action.successCallBack();
    yield put(
      requestGetCountNotificationsInboxes({
        inboxes,
        transactionAreaInboxes,
        isPriorityClaimsManager: userPrivileges.userHasPrivilege(
          PRIVILEGES.ISPRIORITYCLAIMSMANAGER,
        ),
      }),
    );
  }
}
export function* requestRefreshManagerSaga() {
  yield takeLeading(REQUEST_REFRESH_MANAGER, requestRefreshManagerInfo);
}
export function* requestGetNotificationsInboxe(action: any) {
  const { payload } = action;
  const path = `/corporeal/notification-manager/get-inboxes?inboxes=[${payload.inboxes}]&transactionAreaInboxes=[${payload.transactionAreaInboxes}]&isPriorityClaimsManager=${payload.isPriorityClaimManager}`;

  const token = get(userToken.get(), 'token');

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setNotificationsInboxes,
    undefined,
    false,
    LOADERS.NOTIFICATION_INBOXES,
  );
}
export function* requestGetNotificatiosInboxesSaga() {
  yield takeLeading(
    REQUEST_GET_NOTIFICATIONS_INBOXES,
    requestGetNotificationsInboxe,
  );
}
export function* requestGetCountNotificationsInboxe(action: any) {
  const { payload } = action;
  const path = `/corporeal/notification-manager/get-count-affected-inboxes?inboxes=[${payload.inboxes}]&transactionAreaInboxes=[${payload.transactionAreaInboxes}]&isPriorityClaimsManager=${payload.isPriorityClaimsManager}`;

  const token = get(userToken.get(), 'token');

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setCountNotificationsManager,
    undefined,
    false,
    LOADERS.NOTIFICATION,
  );
}

export function* requestGetCountNotificatiosInboxesSaga() {
  yield takeLeading(
    REQUEST_GET_COUNT_NOTIFICATIONS_MANAGER,
    requestGetCountNotificationsInboxe,
  );
}

export function* requestGetInboxesLawyerNotifications(): Generator<unknown> {
  const path = `/corporeal/notification-lawyer/get-inboxes-lawyer-notifications`;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setInboxesLawyerNotifications,
    undefined,
    false,
    LOADERS.NOTIFICATION,
  );
}

export function* requestGetInboxesLawyerNotificationsSaga() {
  yield takeLeading(
    REQUEST_GET_INBOXES_LAWYER_NOTIFICATIONS,
    requestGetInboxesLawyerNotifications,
  );
}

export function* requestGetCountInboxesLawyerNotifications(): Generator<
  unknown
> {
  const path = `/corporeal/notification-lawyer/count-inboxes-lawyer-notifications`;

  const token = yield select(makeSelectKeycloakToken());

  if (isNull(token)) return;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  yield callApi(
    path,
    {},
    options,
    [],
    setCountInboxesLawyerNotifications,
    undefined,
    false,
    LOADERS.NOTIFICATION,
  );
}

export function* requestGetCountInboxesLawyerNotificationsSaga() {
  yield takeLeading(
    REQUEST_GET_COUNT_INBOXES_LAWYER_NOTIFICATIONS,
    requestGetCountInboxesLawyerNotifications,
  );
}

export default [
  requestListingsSaga,
  requestIntervenantsSaga,
  getLawyerNotificationsSaga,
  getManagerNotificationsSaga,
  readLawyerNotificationSaga,
  readManagerNotificationSaga,
  getExpertNotificationsSaga,
  readExpertNotificationSaga,
  getNotificationOpeningManagerMixedClaimSaga,
  requestForceUnlockClaimSaga,
  requestReadConvertClaimNotificationSaga,
  requestRefreshManagerSaga,
  requestGetNotificatiosInboxesSaga,
  requestLinkToBeneficiaryCodesSaga,
  requestGetCountNotificatiosInboxesSaga,
  requestGetInboxesLawyerNotificationsSaga,
  requestGetCountInboxesLawyerNotificationsSaga,
];
